import { Checkbox, TableCell } from '@mui/material';
import React from 'react';

import { tableTypes, UIMenu } from '@/shared/components';
import { globalEnums, globalTypes } from '@/shared/duck';

import { StyledBodyRow } from './body-row.styled.ts';
import { UITableCell } from './components';

interface BodyRowProps<DataItem> extends Partial<tableTypes.MuiTableContentProps<DataItem>> {
  rowKey: globalTypes.UID,
  dataItem: DataItem,
  columns: tableTypes.ColumnProps<DataItem>[];
  index: number,
}

const BodyRow = <DataItem extends globalTypes.UIDItem>({
  rowKey,
  dataItem,
  columns,
  index,
  itemActions,
  onRowClick,
  itemSelection,
  selectedRowId,
  disableRow,
}: BodyRowProps<DataItem>) => {
  const itemSelectionApi = itemSelection?.itemSelectionApi;
  const selectable = Boolean(itemSelectionApi);
  const isSelected = !!itemSelectionApi?.selectedItems.has(dataItem.id) || selectedRowId === dataItem.id;
  const withClickableRows = React.useMemo(() => {
    return !!onRowClick;
  }, [onRowClick]);
  const isRowDisabled = React.useMemo(() => {
    return disableRow ? disableRow(dataItem) : false;
  }, [dataItem, disableRow]);

  const getOnRowClick = React.useCallback(
    (dataItem: DataItem) => () => {
      return dataItem && !isRowDisabled && onRowClick && onRowClick(dataItem);
    },
    [onRowClick, isRowDisabled]
  );

  const getOnToggleCheckRow = React.useCallback(
    (item: DataItem) => () => itemSelectionApi?.onToggleItem(item),
    [itemSelectionApi]
  );

  const isComplexMenu = React.useMemo(() => {
    if (itemActions?.length) {
      return itemActions(dataItem).footerItems?.length ||
             itemActions(dataItem).headerText?.length ||
             itemActions(dataItem).headerSubText?.length ||
             itemActions(dataItem).sx;
    } else {
      return false;
    }
  }, [itemActions, dataItem]);

  return (
    <StyledBodyRow
      key={rowKey}
      hover={withClickableRows && !isRowDisabled}
      selected={isSelected}
      disabled={isRowDisabled}
      clickable={!!onRowClick}
      onClick={getOnRowClick(dataItem)}
    >
      {selectable ? (
        <TableCell padding='checkbox'>
          <Checkbox
            color='primary'
            checked={itemSelectionApi?.selectedItems.has(dataItem.id)}
            slotProps={{
              input: {
                'aria-label': `select_item_${dataItem.id}`,
              }
            }}
            disabled={itemSelection?.disabled || isRowDisabled}
            onChange={getOnToggleCheckRow(dataItem)}
            onClick={(e) => e.stopPropagation()}
          />
        </TableCell>
      ) : null}

      {columns.map((col, colIndex) => (
        <UITableCell
          key={`${index}${colIndex}`}
          dataItem={dataItem}
          column={col}
        />
      ))}

      {itemActions?.length && (
        <TableCell
          onClick={(e: any) => e.stopPropagation()}
          sx={{ textAlign: 'right' }}
        >
          <UIMenu
            type={isComplexMenu
              ? globalEnums.EMenuType.COMPLEX
              : globalEnums.EMenuType.BASIC}
            itemList={itemActions(dataItem).itemList}
            headerText={itemActions(dataItem).headerText}
            headerSubText={itemActions(dataItem).headerSubText}
            footerItems={itemActions(dataItem).footerItems}
            menuIconC={itemActions(dataItem).menuIconC}
            sx={itemActions(dataItem).sx}
          />
        </TableCell>
      )}
    </StyledBodyRow>
  );
};

export default BodyRow;